import { Scalar } from "../../math/math";
import { Bounds } from "../../geometry";
export var NeighborsIndex;
(function (NeighborsIndex) {
    NeighborsIndex[NeighborsIndex["NW"] = 0] = "NW";
    NeighborsIndex[NeighborsIndex["N"] = 1] = "N";
    NeighborsIndex[NeighborsIndex["NE"] = 2] = "NE";
    NeighborsIndex[NeighborsIndex["W"] = 3] = "W";
    NeighborsIndex[NeighborsIndex["C"] = 4] = "C";
    NeighborsIndex[NeighborsIndex["E"] = 5] = "E";
    NeighborsIndex[NeighborsIndex["SW"] = 6] = "SW";
    NeighborsIndex[NeighborsIndex["S"] = 7] = "S";
    NeighborsIndex[NeighborsIndex["SE"] = 8] = "SE";
})(NeighborsIndex || (NeighborsIndex = {}));
export class TileAddress {
    static Split(a, metrics) {
        if (a.levelOfDetail == metrics.maxLOD) {
            return null;
        }
        const baseX = a.x * 2;
        const baseY = a.y * 2;
        const childLod = a.levelOfDetail + 1;
        return [
            new TileAddress(baseX, baseY, childLod),
            new TileAddress(baseX + 1, baseY, childLod),
            new TileAddress(baseX, baseY + 1, childLod),
            new TileAddress(baseX + 1, baseY + 1, childLod),
        ];
    }
    static ShiftMultiple(addresses, N, metrics) {
        const uniqueQuadKeys = new Set();
        addresses.forEach((address) => {
            const shifted = TileAddress.Shift(address, N, metrics);
            if (Array.isArray(shifted)) {
                shifted.forEach((child) => {
                    uniqueQuadKeys.add(child.quadkey);
                });
            }
            else if (shifted) {
                uniqueQuadKeys.add(shifted.quadkey);
            }
        });
        return Array.from(uniqueQuadKeys).map((key) => TileAddress.QuadKeyToTileXY(key));
    }
    static Shift(a, N, metrics) {
        if (Array.isArray(a)) {
            return TileAddress.ShiftMultiple(a, N, metrics);
        }
        let currentKey = a.quadkey;
        let currentLod = a.levelOfDetail;
        if (N === 0) {
            return a;
        }
        if (N > 0) {
            const maxShift = metrics.maxLOD - currentLod;
            const effectiveShift = Math.min(N, maxShift);
            let keys = [currentKey];
            for (let level = 0; level < effectiveShift; level++) {
                keys = keys.flatMap((key) => TileAddress.ToChildsKey(key));
            }
            return keys.map((key) => TileAddress.QuadKeyToTileXY(key));
        }
        const maxShift = currentLod - metrics.minLOD;
        const effectiveShift = Math.min(Math.abs(N), maxShift);
        for (let level = 0; level < effectiveShift; level++) {
            currentKey = TileAddress.ToParentKey(currentKey);
            currentLod--;
        }
        return TileAddress.QuadKeyToTileXY(currentKey);
    }
    static ToBounds(a, metrics) {
        const points = [metrics.getTileXYToPointXY(a.x, a.y), metrics.getTileXYToPointXY(a.x + 1, a.y + 1)];
        return Bounds.FromPoints2(...points);
    }
    static IsEquals(a, b) {
        return a.x === b.x && a.y === b.y && a.levelOfDetail === b.levelOfDetail;
    }
    static IsValidAddress(a, metrics) {
        if (!TileAddress.IsValidLod(a.levelOfDetail, metrics)) {
            return false;
        }
        const s = (0x01 << a.levelOfDetail) - 1;
        if (a.x < 0 || a.x > s) {
            return false;
        }
        if (a.y < 0 || a.y > s) {
            return false;
        }
        return true;
    }
    static AssertValidAddress(a, metrics) {
        if (!TileAddress.IsValidLod(a.levelOfDetail, metrics)) {
            throw new Error(`Invalid levelOfDetail ${a.levelOfDetail}`);
        }
        const s = (0x01 << a.levelOfDetail) - 1;
        if (a.x < 0 || a.x > s) {
            throw new Error(`Invalid x ${a.x}, must be in [0,${s}] range.`);
        }
        if (a.y < 0 || a.y > s) {
            throw new Error(`Invalid y ${a.y}, must be in [0,${s}] range.`);
        }
    }
    static IsValidLod(lod, metrics) {
        return lod >= metrics.minLOD && lod <= metrics.maxLOD;
    }
    static ClampLod(levelOfDetail, metrics) {
        return Scalar.Clamp(levelOfDetail, metrics.minLOD, metrics.maxLOD);
    }
    static ToParentKey(key) {
        return key && key.length > 1 ? key.substring(0, key.length - 1) : key;
    }
    static ToChildsKey(key) {
        key = key || "";
        return [key.slice() + "0", key.slice() + "1", key.slice() + "2", key.slice() + "3"];
    }
    static ToNeighborsKey(key) {
        return TileAddress.ToNeighborsXY(TileAddress.QuadKeyToTileXY(key)).map((a) => (a ? TileAddress.TileXYToQuadKey(a.x, a.y, a.levelOfDetail) : null));
    }
    static ToNeighborsXY(a) {
        const max = Math.pow(2, a.levelOfDetail);
        const n = [
            new TileAddress(a.x - 1, a.y - 1, a.levelOfDetail),
            new TileAddress(a.x, a.y - 1, a.levelOfDetail),
            new TileAddress(a.x + 1, a.y - 1, a.levelOfDetail),
            new TileAddress(a.x - 1, a.y, a.levelOfDetail),
            a,
            new TileAddress(a.x + 1, a.y, a.levelOfDetail),
            new TileAddress(a.x - 1, a.y + 1, a.levelOfDetail),
            new TileAddress(a.x, a.y + 1, a.levelOfDetail),
            new TileAddress(a.x + 1, a.y + 1, a.levelOfDetail),
        ];
        return n.map((ad) => (ad.x >= 0 && ad.y >= 0 && ad.x < max && ad.y < max ? ad : null));
    }
    static TileXYToQuadKey(x, y, levelOfDetail) {
        let quadKey = "";
        for (let i = levelOfDetail; i > 0; i--) {
            let digit = 0;
            const mask = 1 << (i - 1);
            if ((x & mask) != 0) {
                digit++;
            }
            if ((y & mask) != 0) {
                digit++;
                digit++;
            }
            quadKey = quadKey + digit;
        }
        return quadKey;
    }
    static QuadKeyToTileXY(quadKey) {
        let tileX = 0;
        let tileY = 0;
        const levelOfDetail = quadKey.length;
        for (let i = levelOfDetail; i > 0; i--) {
            const mask = 1 << (i - 1);
            switch (quadKey[levelOfDetail - i]) {
                case "0":
                    break;
                case "1":
                    tileX |= mask;
                    break;
                case "2":
                    tileY |= mask;
                    break;
                case "3":
                    tileX |= mask;
                    tileY |= mask;
                    break;
                default:
                    throw new Error("Invalid QuadKey digit sequence.");
            }
        }
        return { x: tileX, y: tileY, levelOfDetail: levelOfDetail };
    }
    constructor(x, y, levelOfDetail) {
        this._x = x;
        this._y = y;
        this._lod = levelOfDetail;
    }
    get x() {
        return this._x;
    }
    set x(value) {
        if (this._x !== value) {
            this._x = value;
            this._k = undefined;
        }
    }
    get y() {
        return this._y;
    }
    set y(value) {
        if (this._y !== value) {
            this._y = value;
            this._k = undefined;
        }
    }
    get levelOfDetail() {
        return this._lod;
    }
    set levelOfDetail(value) {
        if (this._lod !== value) {
            this._lod = value;
            this._k = undefined;
        }
    }
    get quadkey() {
        if (!this._k) {
            this._k = TileAddress.TileXYToQuadKey(this.x, this.y, this.levelOfDetail);
        }
        return this._k;
    }
    clone() {
        return new TileAddress(this.x, this.y, this.levelOfDetail);
    }
    toString() {
        return `x:${this.x}, y:${this.y}, lod:${this.levelOfDetail}, k:${this.quadkey}`;
    }
}
//# sourceMappingURL=tiles.address.js.map